import { Button, Modal, TextBlock, TextInput } from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";

import style from "./file-sharing.scss";
import { logger } from "utils/logging";

import testIds from "testIds.json";

interface Props {
    presignedUrl?: string;
    isLoading: boolean;
    isOpen: boolean;
    onClose: () => void;
}

export const COPY_FEEDBACK_DURATION_MS = 2_000;

const GetDownloadLinkModal = (props: Props): JSX.Element => {
    const { presignedUrl, isLoading, isOpen, onClose } = props;
    const { t } = useTranslation();
    const [copied, setCopied] = React.useState(false);
    const [copyError, setCopyError] = React.useState<string | undefined>();
    const timerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    React.useEffect(() => {
        if (!isOpen) {
            setCopied(false);
            setCopyError(undefined);
        }
    }, [isOpen]);

    React.useEffect(() => () => clearTimeout(timerRef.current), []);

    const handleCopy = React.useCallback(() => {
        if (!presignedUrl) {
            return;
        }
        void navigator.clipboard
            .writeText(presignedUrl)
            .then(() => {
                clearTimeout(timerRef.current);
                setCopied(true);
                setCopyError(undefined);
                timerRef.current = setTimeout(() => setCopied(false), COPY_FEEDBACK_DURATION_MS);
            })
            .catch((error: Error) => {
                logger.error("Failed to copy download link to clipboard", error);
                setCopyError(t("Common.copyButtonFailedText"));
            });
    }, [presignedUrl, t]);

    return (
        <Modal
            isOpen={isOpen}
            hideModal={onClose}
            title={t("FileSharing.downloadLink.title")}
            className={style.dialog}
            buttons={
                <>
                    <Button variant="Secondary" onClick={onClose} testId={testIds.common.dialog.closeButton}>
                        {t("Common.close")}
                    </Button>
                    <Button
                        variant="Primary"
                        onClick={handleCopy}
                        isLoading={isLoading}
                        disabled={!presignedUrl || isLoading}
                        testId={testIds.workArea.fileSharing.downloadLinkDialog.copyButton}
                    >
                        {copied ? t("Common.copied") : t("Common.downloadLink")}
                    </Button>
                </>
            }
        >
            <TextBlock>{t("FileSharing.downloadLink.description")}</TextBlock>
            <TextInput
                name="download-link"
                hideLabelSection
                readOnly
                aria-label={t("FileSharing.downloadLink.title")}
                placeholder={isLoading ? t("FileSharing.downloadLink.urlPlaceholder") : undefined}
                value={presignedUrl ?? ""}
                error={copyError}
                className={style.fullWidthInputWrapper}
                testIds={{ input: testIds.workArea.fileSharing.downloadLinkDialog.linkInput }}
            />
        </Modal>
    );
};

export default GetDownloadLinkModal;
